import type { OperationAvailability } from './operations';

export const CAPABILITY_TOPICS = ['core-editing', 'data-bindings', 'brand', 'animation'] as const;

export type CapabilityTopic = (typeof CAPABILITY_TOPICS)[number];

type CapabilitySpec = {
  topic: CapabilityTopic;
  summary: string;
  readTypes: string[];
  operations: string[];
  unsupported: string[];
  recipe: string;
};

const SPECS: CapabilitySpec[] = [
  {
    topic: 'core-editing',
    summary:
      'Rename the template, move or edit elements, and change the background on the open working copy.',
    readTypes: [
      'document',
      'selection',
      'element',
      'preview',
      'activity',
      'saved_versions',
      'saved_version_preview',
    ],
    operations: [
      'set_name',
      'commit_working_copy',
      'stage_saved_version',
      'set_background',
      'select_element',
      'rename_element',
      'move_element',
      'reorder_element',
      'remove_element',
      'add_element',
      'update_element_data',
      'set_element_locked',
      'set_element_hidden',
    ],
    unsupported: ['group_elements'],
    recipe:
      'Read document, then apply one working-copy operation or a changes array of them. Use readType preview after visual edits; the JPEG is only in tool content, not in structuredContent. Selection does not change workingRevision. A failed changes call restores the working copy. For text fonts, update_element_data must send fontFamily with fontWeight, fontStyle, fontStretch, and fontVariant together. Do not guess variant names such as regular; use a variant from the brand read or copy an existing element style. For IMAGE content, read contentTemplate and write the full URL template back; token-only patches are rejected. Call commit_working_copy only when the user asked to save. The first call opens Confirm Save and returns an instruction to wait. Call it again with userConfirmed true only if the user explicitly asked to save, or with cancel true to close the dialog without saving. Grouping is unsupported.',
  },
  {
    topic: 'data-bindings',
    summary:
      'Choose a source family, template type, market, and element data points for the open template.',
    readTypes: ['source_options', 'market_options', 'data_points', 'advertisers', 'document'],
    operations: [
      'add_data_source',
      'remove_data_source',
      'set_element_sources',
      'remove_element_source',
      'set_display_options',
      'set_element_date_format',
      'set_element_odds_format',
      'set_element_penalties_format',
    ],
    unsupported: ['group_elements'],
    recipe:
      'Read source_options, then market_options for the selected template, then data_points for an element type. Advertisers, markets, and data points are read types. search_reference returns the static shape of a data point, advertiser, or brand, not the live rows. Use a changes array to update several elements, or a binding and its content, in one call. add_data_source and remove_data_source stay single calls. A failed step restores the working copy.',
  },
  {
    topic: 'brand',
    summary:
      'Read the loaded brand or brand kit and set the advertiser or brand kit on the working copy.',
    readTypes: ['brand', 'advertisers'],
    operations: ['set_business_entity', 'set_brand_kit'],
    unsupported: ['group_elements'],
    recipe:
      'On create or duplicate, read advertisers and pick one with set_business_entity as its own apply call, not inside changes. The id must appear in the advertisers read. set_business_entity is rejected on edit. That call loads the advertiser fonts when no shadow brand is set, then sets businessEntityId. Read brand again. Pick fontFamily from fontFamilies and fontVariant with matching weight, style, and stretch from that family variants list. Save rejects invented labels such as regular when they are not listed. On edit, advertisers returns only the current id. Do not expect secret URLs.',
  },
  {
    topic: 'animation',
    summary:
      'Apply, edit, and remove entrance and exit preset effects, then check the motion with a frame, contact sheet, or review video.',
    readTypes: ['animation', 'preview'],
    operations: ['apply_animation_effect', 'update_animation_effect', 'remove_animation_effect'],
    unsupported: ['group_elements', 'timeline_editing', 'raw_track_editing'],
    recipe:
      'Read document for element ids, then read animation for the current effects. Apply apply_animation_effect with targetId and presetId from presets; params are optional and use seconds for duration and start and pixels for distance. Pass expectedWorkingRevision to reject a stale write. The same preset on the same target returns ANIMATION_DUPLICATE. A preset with the same phase and property replaces the old effect and lists it in replaced. Use update_animation_effect with effectId and params or presetId, and remove_animation_effect with effectId. Check the motion with readType preview and args.mode frame (timeSeconds), contact_sheet (optional timestamps, default 8, at most 12), or video (optional startSeconds, endSeconds, fps up to 30, format mp4 or webm, at most 15 seconds). Images are PNG content blocks. Video up to 6 MB is an embedded resource; larger video is an artifactUrl when one is available, otherwise ARTIFACT_TOO_LARGE. Lower fps, shorten the range, or set scale to fit. Timeline, tracks, and keyframes are read-only. Save with commit_working_copy as in core-editing.',
  },
];

const DEFAULT_AVAILABILITY: OperationAvailability = { animationsEnabled: false };

function topicAvailable(topic: CapabilityTopic, availability: OperationAvailability): boolean {
  return topic !== 'animation' || availability.animationsEnabled;
}

export function availableCapabilityTopics(
  availability: OperationAvailability = DEFAULT_AVAILABILITY,
): CapabilityTopic[] {
  return CAPABILITY_TOPICS.filter((topic) => topicAvailable(topic, availability));
}

export function capabilityIndex(
  availability: OperationAvailability = DEFAULT_AVAILABILITY,
): Array<{ topic: CapabilityTopic; summary: string }> {
  return SPECS.filter((spec) => topicAvailable(spec.topic, availability)).map(
    ({ topic, summary }) => ({ topic, summary }),
  );
}

export function capabilitySpec(topic: string): CapabilitySpec | null {
  return SPECS.find((spec) => spec.topic === topic) ?? null;
}
